import Placing from "@/app/ui/Common/Placing";
import { paddingNum, generateSilkImagePath, getSilkImagePath, generateHorseProfileUrl } from "@/app/utils/Common";
import { formattedDate } from "@/app/utils/DayJS";
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableFooter from '@mui/material/TableFooter';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid';
import React, { useEffect } from 'react';
import styled from "styled-components";
import { LANGTYPE_EN, useLanguage } from "@/app/utils/LanguageContext";
import { useMyContext } from "@/app/utils/MyContext";
import _ from 'lodash';
import { textSize, text_size } from "@/app/utils/MixinSize";
import { runnerStatus } from "@/app/utils/constant/statusSet";

const HorseLink = styled.a`
  color: #122C68;
  &:hover {
    text-decoration: underline;
  }
`;

const ResultToggleCell = styled(TableCell)(() => ({
  [`&`]: {
      backgroundColor: "#FFFFFF",
      height: "32px",
      color: "#122C68",
      ...textSize(13),
      fontWeight: "700",
      cursor: "pointer",
      padding: "0px",
      textAlign: "center",
  }
}));

const ResultHeader = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    color: "#333333",
    backgroundColor: "#E1E1E1",
    fontWeight: 400,
    padding: "8px",
    ...textSize(11),
    textAlign: "center",
    height: "32px",
    lineHeight: "14px",
  }
}));

const ResultTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(even)': {
      backgroundColor: "#F4F4F4",
  },
  '&:nth-of-type(odd)': {
      backgroundColor: "#FFFFFF",
  }
}));

const ResultTableCell = styled(TableCell)(() => ({
    [`&`]: {
      color: "#333333",
      fontWeight: 500,
      padding: "8px",
      ...textSize(13),
      textAlign: "center",
      verticalAlign: "middle",
      height: "44px",
      lineHeight: "17px",
    }
  }));

const MResult = styled(Grid)(() => ({
  [`&`]: {
    gap: "8px",
  }
}));

const MResultGrid = styled(Grid)(() => ({
  [`&`]: {
    background: "#FFFFFF",
    width: "100%",
    padding: "8px 8px 12px 8px",
    borderRadius: "4px",
    boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.1)",
    textAlign: "left",
  }
}));

const MResultLabel = styled.span`
    color: #122C68;
    font-weight: 300;
    line-height: 14px;
`;

const MResultTop = styled(Table)(() => ({
  [`td`]: {
    color: "#09254E",
    ...textSize(15),
    fontWeight: "500",
    lineHeight: "22px",
    verticalAlign: "middle",
    padding: "0px",
    border: "0",
  },
  [`td:first-child`]: {
    width: "20%",
  },
  [`td:last-child`]: {
    width: "80%",
    paddingLeft: "8px",
  }
}));

const MResultMiddle = styled(Table)(() => ({
  [`&`]: {
    textAlign: "left",
    margin: "8px 0px",
  },
  [`td`]: {
    ...textSize(13),
    fontWeight: "500",
    verticalAlign: "top",
    padding: "0px 8px",
    border: "0",
  },
  [`tr:first-of-type td:first-of-type`]: {
    width: "20%",
    textAlign: "right",
  },
  [`tr:first-of-type td:nth-child(4)`]: {
    width: "15%",
    borderLeft: "1px solid var(--Stroke, #B4B4B4)"
  }
}));

const MResultBottom = styled(Table)(() => ({
  [`&`]: {
    textAlign: "left",
    padding: "0px 8px",
  },
  [`td`]: {
    ...textSize(13),
    color: "#333333",
    fontWeight: "500",
    verticalAlign: "top",
    padding: "8px 4px 0px 4px",
    border: "0",
    borderTop: "1px solid var(--Stroke, #B4B4B4)",
  },
  [`td:nth-of-type(3)`]: {
    textAlign: "right",
  },
  [`td:nth-of-type(4)`]: {
    textAlign: "center",
  }
}));

const MResultToggleCell = styled(Grid)(() => ({
  [`&`]: {
    borderRadius: "4px",
    ...textSize(13),
    backgroundColor: "#FFFFFF",
    width: "100%",
    height: "32px",
    color: "#122C68",
    fontWeight: "700",
    cursor: "pointer",
    padding: "0px",
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "8px",
  }
}));

const RemarkWrapper = styled.div`
  padding-top: 12px;
`;
const RemarkText = styled.p`
  word-break: break-all;
  ${text_size(13)}
  font-weight: 500;
`;



type tableColumn = "place"|"horseNo"|"colour"|"horse"|"draw"|"hWt"|"hSex"|"hAge"|"jockey"|"trainer"|"margin"|"winodds";

interface propType {
  setRacesDetailLoading?: (loading: boolean) => void;
  columns?: tableColumn[];
  type?: string;
  raceNo: number;
  disableExpansion?: boolean;
  data: any;
  fullFieldPlacing?: boolean; 
}

const ResultTable: React.FC<propType> = ({
  columns = ["place", "horseNo", "colour", "horse", "draw", "hWt", "hSex", "hAge", "jockey", "trainer", "margin", "winodds"], 
  ...props}: propType) => {
    const { state: lang } = useLanguage();
    const { t } = lang;
    const { state: myContextState } = useMyContext();

    const [isResultExpanded, setIsResultExpanded] = React.useState(false);
    const meetingDate = formattedDate(myContextState.selectedMeetingDate, "YYYYMMDD");
    const meetingVenueCode = myContextState.selectedMeetingVenueCode;

    const isPostRace = myContextState.dataSource === 'qids' || myContextState.selectedMeeting?.isPostRace(myContextState.bizDate);
    
    const RaceResultsmgnInd = (races: any, raceNo: number): string => {
      const raceObj = races.filter((x: any) => x.no == raceNo)[0];
      if (raceObj == null)
          return 'M';

      const resultmgnInd = raceObj?.raceResults?.[0]?.mgnInd || 'M';

      return resultmgnInd;
    }
    const isMargin = RaceResultsmgnInd(props.data,props.raceNo) === 'M';

    const runnerFilter = props.data.filter((x: any) => x.no == props.raceNo)?.[0]?.runners?.filter((ru: any)=>![runnerStatus.SCRATCHEDRESERVED].includes(ru.status));

    useEffect(()=>{
      setIsResultExpanded(false);
    }, [myContextState.selectedMeetingVenueCode, myContextState.selectedRaceNo])

    let runners;
    _.forEach(runnerFilter, function(item) {
      item.orderBy1 = paddingNum(item.finalPosition ===0 ? 99 : item.finalPosition);
      item.orderBy2 = paddingNum(item.no);
    });
    if(props.fullFieldPlacing)
    {
      runners = _.orderBy(runnerFilter, ["orderBy1","orderBy2"], ["asc","asc"]);
    }else{
      runners = _.orderBy(runnerFilter?.filter((x: any) => x.finalPosition > 0), ["orderBy1","orderBy2"], ["asc","asc"]);
    }
    
    const runnerCount = runnerFilter?.filter((x: any) => x.status?.toUpperCase().indexOf('SCRATCHED')===-1)?.length;

    const resultViewToggle = () => {
        setIsResultExpanded(!isResultExpanded);
    }

    return (<>
        {props.type === "pc" ?
        <>
          <Table id={`results_table_${props.raceNo}_pc`} sx={{ border: "var(--border)", borderCollapse: "separate", borderRadius: "4px", overflow: "hidden", textAlign: "left !important", boxShadow: "0px 2px 10px 0px rgb(0 0 0 / 10%)"}} aria-label="simple table">
              <TableHead>
                  <TableRow>
                    {
                      columns.map((c: tableColumn) => {
                        switch (c) {
                          case "place":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "56px", textAlign: "left !important" }}>{t('result_pla')}</ResultHeader>
                          case "horseNo":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "52px" }}>{t('result_horse_no')}</ResultHeader>
                          case "colour":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "34px" }}>{t('result_colour')}</ResultHeader>
                          case "horse":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "164px", textAlign: "left !important" }}>{t('result_horse')}</ResultHeader>
                          case "draw":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "48px" }}>{t('result_draw')}</ResultHeader>
                          case "hWt":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "54px" }}>{t('result_wt_lbs')}</ResultHeader>
                          case "hSex":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "28px" }}>{t('result_sex')}</ResultHeader>
                          case "hAge":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "28px" }}>{t('result_age')}</ResultHeader>
                          case "jockey":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "164px", textAlign: "left !important" }}>{t('result_jockey')}</ResultHeader>
                          case "trainer":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "164px", textAlign: "left !important" }}>{t('result_trainer')}</ResultHeader>
                          case "margin":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "115px", textAlign: "left !important" }}>{isMargin ? t('result_margin') : t('result_LBW')}</ResultHeader>
                          case "winodds":
                            return <ResultHeader key={`header_${c}`} sx={{ width: "49px" }}>{t('result_win_odds')}</ResultHeader>
                        }
                      })
                    }
                  </TableRow>
              </TableHead>
              <TableBody>
                  {runners?.map((resultRows: any, index: number) => ( 
                    ((resultRows.finalPosition > 0 && resultRows.finalPosition <=4) || isResultExpanded) &&
                    <ResultTableRow key={`result_row_${index}`}>
                    {
                      columns.map((c: tableColumn) => {
                        switch (c) {
                          case "place":
                            return <ResultTableCell>
                                <Placing
                                    placing={resultRows.finalPosition}
                                    totalRunners={runnerCount}
                                    type={props.type}
                                    runnerStatus={resultRows.status}
                                    deadHeat={resultRows.deadHeat}
                                ></Placing>
                            </ResultTableCell>
                          case "horseNo":
                            return <ResultTableCell sx={{ fontSize: "15px !important", [`@media only screen and (min-width: 1680px)`]: { fontSize: "17px !important",} }}>
                              {resultRows.no}
                              </ResultTableCell>
                          case "colour":
                            return <ResultTableCell>
                              <img style={{width: "30px", height: "30px"}} 
                                src={generateSilkImagePath(getSilkImagePath(meetingDate, meetingVenueCode, props.raceNo, resultRows.no), lang.langCode)} 
                                alt={getSilkImagePath(meetingDate, meetingVenueCode, props.raceNo, resultRows.no)} 
                                key={getSilkImagePath(meetingDate, meetingVenueCode, props.raceNo, resultRows.no)} 
                                onError={(e:any) => e.target.style.display = 'none'} /> 
                              </ResultTableCell>
                          case "horse":
                            return <ResultTableCell sx={{ textAlign: "left !important"}}>
                              {isPostRace ? <p style={{color: "#122C68"}}>{lang.langType === LANGTYPE_EN ? resultRows?.horse?.name_en : resultRows?.horse?.name_ch}</p> : 
                                <HorseLink target="_blank" href={generateHorseProfileUrl(
                                lang.langCode, 
                                myContextState.selectedMeetingDate, 
                                myContextState.selectedMeeting?.meetingVenueCode || '', 
                                props.raceNo.toString(), 
                                resultRows.id, 
                                resultRows.no)}>{lang.langType === LANGTYPE_EN ? resultRows?.horse?.name_en : resultRows?.horse?.name_ch}</HorseLink>
                              } 
                              </ResultTableCell>
                          case "draw":
                            return <ResultTableCell>{resultRows?.status.toUpperCase().indexOf('SCRATCHED') === -1 ? resultRows.barrierDrawNumber : "--"}</ResultTableCell>
                          case "hWt":
                            return <ResultTableCell>{resultRows.handicapWeight}</ResultTableCell>
                          case "hSex":
                            return <ResultTableCell>{lang.langType === LANGTYPE_EN ? resultRows?.sexNm?.english : resultRows?.sexNm?.chinese}</ResultTableCell>
                          case "hAge":
                            return <ResultTableCell>{resultRows.age}</ResultTableCell>
                          case "jockey":
                            return <ResultTableCell sx={{ textAlign: "left !important" }}>{lang.langType === LANGTYPE_EN ? resultRows?.jockey?.name_en : resultRows?.jockey?.name_ch}</ResultTableCell>
                          case "trainer":
                            return <ResultTableCell sx={{ textAlign: "left !important" }}>{lang.langType === LANGTYPE_EN ? resultRows?.trainer?.name_en : resultRows?.trainer?.name_ch}</ResultTableCell>
                          case "margin":
                            return <ResultTableCell sx={{ textAlign: "left !important" }}>                            
                              {                            
                                resultRows?.status.toUpperCase().indexOf('SCRATCHED') === -1 
                                ? (lang.langType === LANGTYPE_EN ? (isMargin ? resultRows?.marginNm?.english : resultRows?.lbwNm?.english) : (isMargin ? resultRows?.marginNm?.chinese : resultRows?.lbwNm?.chinese))
                                : "--"
                              }
                            </ResultTableCell>
                          case "winodds":
                            return <ResultTableCell>{resultRows?.status.toUpperCase().indexOf('SCRATCHED') === -1 ? resultRows.winOdds : "--"}</ResultTableCell>
                        }
                      })
                    }
                    </ResultTableRow>
                  ))}
              </TableBody>
              <TableFooter>
                  <TableRow>
                      {
                          !props.disableExpansion ?
                              <ResultToggleCell colSpan={12} id="resultExpandBtn" onClick={resultViewToggle}>
                                  <span style={{ verticalAlign: "middle" }}>{isResultExpanded ? t("viewLess") : t("viewMore")}</span>
                                  <img src={`${myContextState.assetPrefix}/footer/arrow-up.svg`} style={(isResultExpanded ? { verticalAlign: "middle", transform: "rotate(0)" } : { verticalAlign: "middle", transform: "rotate(-180deg)" })} />
                              </ResultToggleCell>
                              : <></>
                      }
                  </TableRow>
              </TableFooter>
          </Table>
          
          {lang.langType === LANGTYPE_EN && myContextState.selectedRaceNo.valueOf()!=-1 ? (
            <RemarkWrapper >
              <RemarkText>
                C=Colt; H=Horse; F=Filly; M=Mare; G=Gelding; R=Rig
              </RemarkText>
            </RemarkWrapper>
          ) : null}
        </>
            :
        <>
          <MResult container>
            {runners?.map((resultRows: any, index: number) => (
                ((resultRows.finalPosition > 0 && resultRows.finalPosition <=4) || isResultExpanded) &&
                  <MResultGrid key={`result_row_${index}`}>
                    <MResultTop>
                      <TableCell>
                        <Placing
                            placing={resultRows.finalPosition}
                            totalRunners={runnerCount}
                            type={props.type}
                            runnerStatus={resultRows.status}
                            deadHeat={resultRows.deadHeat}
                        ></Placing>
                      </TableCell>
                      <TableCell>
                        {resultRows.no}.{" "}
                        {isPostRace ? <span style={{color: "#122C68"}}>{lang.langType === LANGTYPE_EN ? resultRows?.horse?.name_en : resultRows?.horse?.name_ch}</span> : 
                        <HorseLink target="_blank" href={generateHorseProfileUrl(
                          lang.langCode, 
                          myContextState.selectedMeetingDate, 
                          myContextState.selectedMeeting?.meetingVenueCode || '', 
                          props.raceNo.toString(), 
                          resultRows.id, 
                          resultRows.no)}>{lang.langType === LANGTYPE_EN ? resultRows?.horse?.name_en : resultRows?.horse?.name_ch}</HorseLink> }
                      </TableCell>
                    </MResultTop>
                    <MResultMiddle>
                      <TableRow style={{ background: "#FFFFFF" }}>
                        {columns.includes("colour") ? 
                          <TableCell rowSpan={2}>
                            <img style={{width: "30px", height: "30px"}} 
                              src={generateSilkImagePath(getSilkImagePath(meetingDate, meetingVenueCode, props.raceNo, resultRows.no), lang.langCode)} 
                              alt={getSilkImagePath(meetingDate, meetingVenueCode, props.raceNo, resultRows.no)} 
                              key={getSilkImagePath(meetingDate, meetingVenueCode, props.raceNo, resultRows.no)} 
                              onError={(e:any) => e.target.style.display = 'none'} />
                          </TableCell>
                          : <TableCell rowSpan={2}></TableCell>
                        }
                        {columns.includes("jockey") ? 
                          <>
                            <TableCell><MResultLabel style={{ width: "15%" }}>{t('result_jockey')}</MResultLabel></TableCell>
                            <TableCell style={{ width: "50%" }}>
                            {lang.langType === LANGTYPE_EN ? resultRows?.jockey?.name_en : resultRows?.jockey?.name_ch}
                            </TableCell>
                          </>
                          :
                          <>
                            <TableCell><MResultLabel style={{ width: "15%" }}></MResultLabel></TableCell>
                            <TableCell style={{ width: "50%" }}></TableCell>
                          </>
                        }
                        {columns.includes("draw") ? 
                          <TableCell rowSpan={2}>
                            <MResultLabel>{t('result_draw')}</MResultLabel><br/>
                            {resultRows?.status.toUpperCase().indexOf('SCRATCHED') === -1 ? resultRows.barrierDrawNumber : "--"}
                          </TableCell>
                          : <></>
                        }
                      </TableRow>
                      <TableRow>
                        {columns.includes("trainer") ? 
                          <>
                            <TableCell><MResultLabel>{t('result_trainer')}</MResultLabel></TableCell>
                            <TableCell>{lang.langType === LANGTYPE_EN ? resultRows?.trainer?.name_en : resultRows?.trainer?.name_ch}</TableCell>
                          </> : <></>
                        }
                      </TableRow>
                    </MResultMiddle>
                    {columns.includes("margin") || columns.includes("winodds") ? 
                      <MResultBottom>
                        <TableRow style={{ background: "#FFFFFF" }}>
                          {columns.includes("margin") ? 
                            <>
                              <TableCell style={{ width: "20%" }}><MResultLabel>{isMargin ? t('result_margin') : t('result_LBW')}</MResultLabel></TableCell>
                              <TableCell style={{ width: "35%" }}>
                                { 
                                  resultRows?.status.toUpperCase().indexOf('SCRATCHED') === -1 
                                  ? (lang.langType === LANGTYPE_EN ? (isMargin ? resultRows?.marginNm?.english : resultRows?.lbwNm?.english) : (isMargin ? resultRows?.marginNm?.chinese : resultRows?.lbwNm?.chinese))
                                  : "--"
                                }
                              </TableCell>
                            </>
                            :
                            <>
                              <TableCell style={{ width: "20%" }}></TableCell>
                              <TableCell style={{ width: "35%" }}></TableCell>
                            </>
                          }
                          {columns.includes("winodds") ? 
                            <>
                              <TableCell style={{ width: "30%" }}><MResultLabel>{t('result_win_odds')}</MResultLabel></TableCell>
                              <TableCell style={{ width: "15%" }}>
                                {resultRows?.status.toUpperCase().indexOf('SCRATCHED') === -1 ? resultRows.winOdds : "--"}
                              </TableCell>
                            </>
                            :
                            <>
                              <TableCell style={{ width: "30%" }}></TableCell>
                              <TableCell style={{ width: "15%" }}></TableCell>
                            </>
                          }
                        </TableRow>
                      </MResultBottom>
                      : <></>
                    }
                  </MResultGrid>
              ))}
              {
                !props.disableExpansion && !isResultExpanded ?
                  <MResultToggleCell id="resultExpandBtn" onClick={resultViewToggle}>
                    <span style={{ verticalAlign: "middle" }}>{isResultExpanded ? t("viewLess") : t("viewFullResult")}</span>
                    <img src={`${myContextState.assetPrefix}/footer/arrow-up.svg`} style={(isResultExpanded ? { verticalAlign: "middle", transform: "rotate(0)" } : { verticalAlign: "middle", transform: "rotate(-180deg)" })} />
                  </MResultToggleCell>
                  : <></>
              }
          </MResult>
          
          {lang.langType === LANGTYPE_EN && myContextState.selectedRaceNo.valueOf()!=-1 ? (
            <RemarkWrapper>
              <RemarkText>
                C=Colt; H=Horse; F=Filly; M=Mare; G=Gelding; R=Rig
              </RemarkText>
            </RemarkWrapper>
          ) : null}
        </>
        }
    </>)
}

export default ResultTable;